import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { CategoryTag } from '../common/ui';
import QuoteService from '../../services/QuoteService';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { fmtNumber } from '../../utils/format';

const NONE = 'none';
const keyOf = (category) => (category ? String(category.id) : NONE);
const NUM = /^(\d{4})-(\d+)$/;
/** The next "YYYY-NNNN" after the suggestion and after any number already typed in this dialog. */
const nextAfter = (suggested, taken) => {
    const m = NUM.exec(suggested || '');
    if (!m) return suggested;
    let n = Number(m[2]);
    const used = new Set(taken.map(x => x.trim()).filter(Boolean));
    while (used.has(`${m[1]}-${String(n).padStart(4, '0')}`)) n += 1;
    return `${m[1]}-${String(n).padStart(4, '0')}`;
};

/**
 * Accepted quote → jobs. Open positions are grouped by category, one job per group; positions without a
 * category are assigned to one of the groups (or form their own group when no position has a category).
 * Each job can be left out for now, renamed and given a planned date.
 */
export default function CreateJobsDialog({ open, quote, money, onClose, onDone }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [jobs, setJobs] = useState({});           // groupKey -> { include, title, scheduledDate }
    const [assign, setAssign] = useState({});       // uncategorised itemId -> groupKey
    const [saving, setSaving] = useState(false);
    const [suggested, setSuggested] = useState(null);

    const openItems = useMemo(() => (quote?.items || []).filter(i => !i.job), [quote]);
    const groups = useMemo(() => {
        const map = new Map();
        openItems.filter(i => i.category).forEach(i => {
            if (!map.has(keyOf(i.category))) map.set(keyOf(i.category), { key: keyOf(i.category), category: i.category });
        });
        if (!map.size && openItems.length) map.set(NONE, { key: NONE, category: null });
        return [...map.values()];
    }, [openItems]);
    const loose = groups.some(g => g.key === NONE) ? [] : openItems.filter(i => !i.category);

    useEffect(() => {
        if (!open || !quote) return;
        const single = groups.length === 1;
        setSuggested(null);
        JobService.nextNumber().then(setSuggested).catch(() => {});
        setJobs(Object.fromEntries(groups.map(g => [g.key, {
            include: true, scheduledDate: '', number: '',
            title: single || !g.category ? quote.title : `${quote.title} – ${g.category.name}`,
        }])));
        setAssign(Object.fromEntries(loose.map(i => [i.id, groups[0]?.key])));
    }, [open, quote]); // eslint-disable-line react-hooks/exhaustive-deps

    const itemsOf = (g) => openItems.filter(i => (i.category ? keyOf(i.category) === g.key : (g.key === NONE || assign[i.id] === g.key)));
    const sum = (items) => items.reduce((s, i) => s + Number(i.total), 0);
    const setJob = (key, patch) => setJobs(j => ({ ...j, [key]: { ...j[key], ...patch } }));
    const chosen = groups.filter(g => jobs[g.key]?.include);
    const invalid = chosen.some(g => !jobs[g.key].title.trim());

    const submit = async () => {
        setSaving(true);
        try {
            const payload = chosen.map(g => ({
                categoryId: g.category?.id ?? null,
                itemIds: itemsOf(g).map(i => i.id),
                title: jobs[g.key].title.trim(),
                number: jobs[g.key].number.trim() || null,
                scheduledDate: jobs[g.key].scheduledDate || null,
            }));
            const updated = await QuoteService.createJobs(quote.id, payload);
            showToast(t('quotes.jobs.created', { count: payload.length }), 'success');
            onDone(updated);
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    if (!quote) return null;
    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>{t('quotes.jobs.dialogTitle', { count: groups.length })}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2 }}>{t('quotes.jobs.dialogHint')}</Typography>

                {loose.length > 0 && (
                    <Box sx={{ mb: 2.5, p: 1.75, borderRadius: '12px', bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400E', mb: 1 }}>{t('quotes.jobs.unassignedTitle')}</Typography>
                        {loose.map(i => (
                            <Box key={i.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
                                <Typography sx={{ flex: '1 1 200px', fontSize: 13.5, color: '#1E293B' }}>{i.description}</Typography>
                                <TextField select size="small" value={assign[i.id] ?? ''} onChange={(e) => setAssign(a => ({ ...a, [i.id]: e.target.value }))}
                                           sx={{ minWidth: 220, bgcolor: '#fff' }} label={t('quotes.jobs.goesInto')}>
                                    {groups.map(g => <MenuItem key={g.key} value={g.key}>{g.category?.name}</MenuItem>)}
                                </TextField>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {groups.map(g => {
                        const job = jobs[g.key];
                        if (!job) return null;
                        const items = itemsOf(g);
                        return (
                            <Box key={g.key} sx={{ border: '1px solid', borderColor: job.include ? '#BFD4E2' : '#E2E8F0', borderRadius: '12px', p: 1.75, bgcolor: job.include ? '#fff' : '#F8FAFC', opacity: job.include ? 1 : 0.7 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: job.include ? 1.5 : 0 }}>
                                    <Checkbox checked={job.include} onChange={(e) => setJob(g.key, { include: e.target.checked })} sx={{ p: 0.5 }}
                                              inputProps={{ 'aria-label': t('quotes.jobs.include') }} />
                                    {g.category ? <CategoryTag category={g.category} /> : <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748B' }}>{t('jobs.noCategory')}</Typography>}
                                    <Typography sx={{ fontSize: 12.5, color: '#94A3B8' }}>· {t('quotes.jobs.positions', { count: items.length })}</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>{money.fmt(sum(items))}</Typography>
                                </Box>
                                {job.include && (
                                    <>
                                        <Grid container spacing={1.5}>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                <TextField fullWidth size="small" label={t('jobs.fields.number')} value={job.number}
                                                           onChange={(e) => setJob(g.key, { number: e.target.value })} slotProps={{ htmlInput: { maxLength: 20 } }}
                                                           helperText={suggested && !job.number.trim() ? (
                                                               <Box component="span" role="button" tabIndex={0} sx={{ color: '#427898', fontWeight: 600, cursor: 'pointer' }}
                                                                    onClick={() => setJob(g.key, { number: nextAfter(suggested, Object.values(jobs).map(j => j.number)) })}>
                                                                   {t('jobs.useNextNumber', { number: nextAfter(suggested, Object.values(jobs).map(j => j.number)) })}
                                                               </Box>
                                                           ) : ' '} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 5 }}>
                                                <TextField fullWidth size="small" required label={t('jobs.fields.title')} value={job.title}
                                                           onChange={(e) => setJob(g.key, { title: e.target.value })} error={!job.title.trim()}
                                                           slotProps={{ htmlInput: { maxLength: 200 } }} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 3 }}>
                                                <TextField fullWidth size="small" type="date" label={t('jobs.fields.scheduledDate')} value={job.scheduledDate}
                                                           onChange={(e) => setJob(g.key, { scheduledDate: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
                                            </Grid>
                                        </Grid>
                                        <Box component="ul" sx={{ m: 0, mt: 1.25, pl: 2.5, color: '#475569', fontSize: 13 }}>
                                            {items.map(i => (
                                                <li key={i.id}>
                                                    {i.description}
                                                    <Box component="span" sx={{ color: '#94A3B8' }}>
                                                        {` · ${fmtNumber(i.quantity)}${i.unit ? ` ${i.unit}` : ''} · ${money.fmt(i.total)}`}
                                                    </Box>
                                                </li>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {chosen.length < groups.length && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>{t('quotes.jobs.laterHint')}</Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" color="success" disableElevation onClick={submit} disabled={saving || !chosen.length || invalid} sx={{ minWidth: 140 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('quotes.jobs.submit', { count: chosen.length })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
